import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import type {
  UserRoleRow, UserTeamRow, AuditTrailRow,
  VacantSubstitutionRow, VacantTerritoryRow, Unit,
  ExerciseRow, CustomColumnsRow, ReasonRow, DemotionReasonRow,
} from '../types/api/generated';
import {
  UserRoleRowSchema, UserTeamRowSchema, AuditTrailRowSchema,
  VacantSubstitutionRowSchema, VacantTerritoryRowSchema,
  ExerciseRowSchema, CustomColumnsRowSchema, ReasonRowSchema, DemotionReasonRowSchema,
} from '../types/api/schemas';
import { validateResponse } from './validateResponse';

export const adminApi = createApi({
    reducerPath: 'adminApi',
    baseQuery,
    endpoints: (builder) => ({
        userSearch: builder.mutation<UserRoleRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/search', method: 'POST', body }),
            transformResponse: validateResponse(UserRoleRowSchema.array()),
        }),
        getUserRoles: builder.mutation<UserRoleRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/user-roles', method: 'POST', body }),
            transformResponse: validateResponse(UserRoleRowSchema.array()),
        }),
        getUserTeams: builder.mutation<UserTeamRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/user-teams', method: 'POST', body }),
            transformResponse: validateResponse(UserTeamRowSchema.array()),
        }),
        getAuditTrail: builder.mutation<AuditTrailRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/audit-trail', method: 'POST', body }),
            transformResponse: validateResponse(AuditTrailRowSchema.array()),
        }),
        getVacantSubstitutions: builder.mutation<VacantSubstitutionRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/vacant-substitutions', method: 'POST', body }),
            transformResponse: validateResponse(VacantSubstitutionRowSchema.array()),
        }),
        getVacantTerritories: builder.mutation<VacantTerritoryRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/vacant-territories', method: 'POST', body }),
            transformResponse: validateResponse(VacantTerritoryRowSchema.array()),
        }),
        upsertVacantSubstitution: builder.mutation<Unit, unknown>({
            query: (body) => ({ url: 'api/v2/admin/upsert-vacant-substitution', method: 'POST', body }),
        }),
        deleteVacantSubstitution: builder.mutation<Unit, unknown>({
            query: (body) => ({ url: 'api/v2/admin/delete-vacant-substitution', method: 'POST', body }),
        }),
        getExercises: builder.mutation<ExerciseRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/exerciseadmin/get-exercises', method: 'POST', body }),
            transformResponse: validateResponse(ExerciseRowSchema.array()),
        }),
        getCustomColumns: builder.mutation<CustomColumnsRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/exerciseadmin/get-custom-columns', method: 'POST', body }),
            transformResponse: validateResponse(CustomColumnsRowSchema.array()),
        }),
        getDemotionReasons: builder.mutation<DemotionReasonRow[], unknown>({
            query: (body) => ({ url: 'api/v2/admin/exerciseadmin/get-demotion-reasons', method: 'POST', body }),
            transformResponse: validateResponse(DemotionReasonRowSchema.array()),
        }),
    }),
});

export const {
    useUserSearchMutation,
    useGetUserRolesMutation,
    useGetUserTeamsMutation,
    useGetAuditTrailMutation,
    useGetVacantSubstitutionsMutation,
    useGetVacantTerritoriesMutation,
    useUpsertVacantSubstitutionMutation,
    useDeleteVacantSubstitutionMutation,
    useGetExercisesMutation,
    useGetCustomColumnsMutation,
    useGetDemotionReasonsMutation,
} = adminApi;
